var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
/**
 * Salva la posizione di scroll dei contenitori lista e la ripristina quando
 * l'utente torna indietro dal dettaglio.
 *
 * La posizione è indicizzata per `location.key` (stabile per singola voce di
 * history: il back con navigazione POP riusa la stessa key) e conservata in una
 * mappa a livello di modulo, che sopravvive allo smontaggio della pagina — come
 * il comportamento nativo — ma non a un reload completo.
 */
var scrollPositions = new Map();
// Numero massimo di frame in cui ritentiamo il restore, in attesa che i
// contenuti (risultati da cache, filtri applicati) rendano il contenitore
// abbastanza alto da raggiungere l'offset salvato.
var MAX_RESTORE_FRAMES = 30;
export var useScrollRestoration = function (ref, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.id, id = _c === void 0 ? 'default' : _c, _d = _b.deps, deps = _d === void 0 ? [] : _d, _e = _b.enabled, enabled = _e === void 0 ? true : _e;
    var location = useLocation();
    var navigationType = useNavigationType();
    var storageKey = "".concat(location.key, ":").concat(id);
    // Persiste l'offset mentre l'utente scorre.
    React.useEffect(function () {
        var element = ref.current;
        if (!enabled || !element)
            return;
        var handleScroll = function () {
            scrollPositions.set(storageKey, element.scrollTop);
        };
        element.addEventListener('scroll', handleScroll, { passive: true });
        return function () { return element.removeEventListener('scroll', handleScroll); };
    }, [ref, storageKey, enabled]);
    // Ripristina l'offset al (ri)montaggio, solo su navigazione POP (back/forward).
    var restoredKey = React.useRef(null);
    React.useLayoutEffect(function () {
        var element = ref.current;
        if (!enabled || !element)
            return;
        // Le navigazioni "in avanti" (PUSH/REPLACE) devono partire dall'inizio.
        if (navigationType !== 'POP') {
            restoredKey.current = storageKey;
            return;
        }
        if (restoredKey.current === storageKey)
            return;
        var target = scrollPositions.get(storageKey);
        if (target == null)
            return;
        var rafId = 0;
        var frames = 0;
        var restore = function () {
            var node = ref.current;
            if (!node)
                return false;
            node.scrollTop = target;
            // Raggiunto: il contenuto è abbastanza alto e l'offset è applicato.
            return Math.abs(node.scrollTop - target) <= 1;
        };
        // Tentativo sincrono (prima del paint): evita il flash in cima quando i
        // risultati sono già disponibili da cache.
        if (restore()) {
            restoredKey.current = storageKey;
            return;
        }
        // Il contenuto non è ancora abbastanza alto: ritenta a ogni frame finché
        // l'offset diventa raggiungibile.
        var retry = function () {
            frames += 1;
            if (restore()) {
                restoredKey.current = storageKey;
                return;
            }
            if (frames < MAX_RESTORE_FRAMES) {
                rafId = requestAnimationFrame(retry);
            }
        };
        rafId = requestAnimationFrame(retry);
        return function () { return cancelAnimationFrame(rafId); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, __spreadArray([ref, storageKey, enabled, navigationType], deps, true));
};
